import React, { useState } from "react";
import { Copy } from "lucide-react";
import CafeQR from "../components/CafeQR";

/**
 * Creator-flavored alternative to the cafe table-tent block.
 * Same QR, but instead of a printable PDF we surface three pre-written
 * social copy snippets the creator can paste straight into their bio,
 * caption, or story.
 */
export default function CreatorShareKit({ cafe, cafeUrl }) {
  const [copiedKey, setCopiedKey] = useState(null);
  const [qrLight, setQrLight] = useState(false); // dark by default — fits IG story aesthetic

  const firstName = (cafe?.name || "your friend").split(/\s+/)[0];
  const SNIPPETS = [
    {
      key: "bio",
      label: "// short · for your bio",
      sub: "instagram bio · tiktok bio · linktree headline",
      text: `i read cups with soma lixir 🌊 yours w/ my link ↓\n${cafeUrl}`,
    },
    {
      key: "caption",
      label: "// caption · for a post",
      sub: "instagram caption · tiktok caption",
      text: `i've been doing this thing where i finish my coffee or tea, snap the inside of my cup, and three minutes later i get a reading written like a letter from someone who actually paid attention.\n\nit's called soma lixir. try yours with my link — i get 44% and 20% goes to starbucks workers united.\n\n${cafeUrl}`,
    },
    {
      key: "story",
      label: "// story · slower + softer",
      sub: "instagram story · tiktok hook · email signature",
      text: `your cup is empty. what remains is not.\n\ni'm reading cups with soma lixir — try yours through me:\n${cafeUrl}\n\n— ${firstName}`,
    },
  ];

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // fallback for old browsers / iOS quirks
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1600);
  };

  return (
    <div style={{ marginBottom: 32 }} data-testid="dashboard-creator-kit">
      <p className="section-marker" style={{ marginBottom: 12 }}>your share kit</p>
      <p style={{ color: "var(--cream-soft)", fontStyle: "italic", fontFamily: "'IM Fell DW Pica', serif", fontSize: 15, margin: "0 0 18px", lineHeight: 1.5 }}>
        Three voices for three places. Tap copy on the one that fits, paste it into your story, caption, or bio. Your link auto-fills the cafe attribution — you earn 44% on every reading scanned through you.
      </p>

      {/* QR PREVIEW + download */}
      <div style={{ display: "flex", justifyContent: "center", padding: "6px 0 12px" }}>
        <CafeQR
          url={cafeUrl}
          size={180}
          filename={`soma-lixir-${cafe.slug}-qr.png`}
          mode={qrLight ? "light" : "dark"}
          testId="dashboard-qr"
          showDownloadBtn={true}
        />
      </div>
      <div style={{ display: "flex", gap: 6, marginBottom: 22, maxWidth: 320, margin: "0 auto 22px" }} data-testid="qr-mode-toggle">
        <button type="button" data-testid="qr-mode-dark" onClick={() => setQrLight(false)} className={`mono-btn ${!qrLight ? "active" : ""}`} style={{ flex: 1 }}>
          dark · brand-true
        </button>
        <button type="button" data-testid="qr-mode-light" onClick={() => setQrLight(true)} className={`mono-btn ${qrLight ? "active" : ""}`} style={{ flex: 1 }}>
          light · clean overlay
        </button>
      </div>

      {/* SNIPPETS */}
      <div style={{ display: "grid", gap: 12 }} data-testid="creator-snippets">
        {SNIPPETS.map((s) => {
          const wasCopied = copiedKey === s.key;
          return (
            <div
              key={s.key}
              data-testid={`creator-snippet-${s.key}`}
              style={{
                padding: "14px 16px",
                background: "rgba(143, 196, 224, 0.06)",
                border: "1px solid rgba(143, 196, 224, 0.22)",
                borderRadius: 0,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, marginBottom: 4 }}>
                <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--terracotta-soft)" }}>
                  {s.label}
                </span>
                <button
                  type="button"
                  data-testid={`creator-snippet-copy-${s.key}`}
                  onClick={() => copy(s.text, s.key)}
                  className="mono-btn"
                  style={{ padding: "4px 10px" }}
                >
                  <Copy size={10} style={{ verticalAlign: "middle", marginRight: 5 }} />
                  {wasCopied ? "COPIED" : "COPY"}
                </button>
              </div>
              <p style={{ margin: "0 0 8px", fontSize: 11, color: "var(--dim)", fontFamily: "'Space Mono', monospace" }}>
                {s.sub}
              </p>
              <pre
                style={{
                  margin: 0,
                  whiteSpace: "pre-wrap",
                  fontFamily: "'IM Fell DW Pica', serif",
                  fontSize: 14,
                  color: "var(--cream)",
                  lineHeight: 1.55,
                  fontStyle: "italic",
                  letterSpacing: "0.005em",
                }}
              >
                {s.text}
              </pre>
            </div>
          );
        })}
      </div>

      <p style={{ textAlign: "center", marginTop: 16, fontFamily: "'IM Fell DW Pica', serif", fontStyle: "italic", fontSize: 12, color: "var(--dim)", lineHeight: 1.4 }}>
        Every reading scanned through your link = $2.44 to you · $1.11 to Starbucks Workers United · $2.00 to Soma
      </p>
    </div>
  );
}
